import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import functions from "../../../util/functions";
import { numberFormat } from "../../../util/araknerdlib";
import DashCardHeader from "../../Dashboard/DashCardHeader";

const BalanceStatistics = ({
  totalBilled = 0,
  totalPaid = 0,
  grossBalance = 0,
  currentBalance = 0,
  defaulters = 0,
  currency
}) => {
  const { percentagePaid, outstanding } = useMemo(() => {
    const billed = Number(totalBilled) || 0;
    const paid = Number(totalPaid) || 0;
    const out = Math.max(0, billed - paid);
    const pct = billed > 0 ? ((paid / billed) * 100).toFixed(1) : "0.0";
    return { percentagePaid: pct, outstanding: out };
  }, [totalBilled, totalPaid]);

  const series = useMemo(() => [outstanding, Number(totalPaid) || 0], [outstanding, totalPaid]);

  const chartOptions = useMemo(
    () => ({
      chart: { type: "donut", sparkline: { enabled: true } },
      labels: ["Outstanding", "Settled"],
      colors: ["#E2E8F0", "#4F46E5"], // slate + theme indigo
      stroke: { width: 0 },
      dataLabels: { enabled: false },
      legend: { show: false },
      plotOptions: {
        pie: {
          donut: {
            size: "78%",
            labels: {
              show: true,
              name: {
                show: true,
                offsetY: -4,
                color: "#64748B",
                fontSize: "12px",
                fontWeight: 800,
              },
              value: {
                show: true,
                offsetY: 8,
                color: "#0F172A",
                fontSize: "23px",
                fontWeight: 900,
              },
              total: {
                show: true,
                label: 'Settled',
                color: "#0F172A",
                fontSize: "18px",
                fontWeight: 100,
                formatter: () => `${percentagePaid}%`,
              },
            },
          },
        },
      },
      tooltip: {
        y: { formatter: (val) => `${currency} ${functions.numberFormat(val)}` },
      },
    }),
    [percentagePaid, currency]
  );

  return (
    <>
      <style>{`

        .bs-metrics{ margin-top: 14px; display:flex; flex-direction:column; gap: 10px; }
        .bs-metric{
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding: 12px 14px;
          border-radius: 14px;
          border: 1px solid rgba(15,23,42,.06);
          background: rgba(15,23,42,.02);
        }
        .bs-label{
          font-size: 12px;
          font-weight: 800;
          color: rgba(71,85,105,.95);
          text-transform: none; /* ✅ no ugly uppercasing */
          letter-spacing: 0;
        }
        .bs-value{
          font-weight: 900;
          color: #0F172A;
        }

        /* Indigo “soft” variants (theme matching) */
        .bs-metric.indigo{
          background: rgba(79,70,229,.06);
          border-color: rgba(79,70,229,.12);
        }
        .bs-metric.indigo .bs-value{ color: #4F46E5; }

        .bs-pill{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-width: 34px;
          height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          background: rgba(79,70,229,.12);
          border: 1px solid rgba(79,70,229,.16);
          color: #4F46E5;
          font-weight: 900;
          font-size: 12px;
        }

        .bs-divider{
          height: 1px;
          background: rgba(15,23,42,.06);
          margin: 14px 0 4px;
        }
      `}</style>

      <DashCardHeader title="Collection Efficiency" description="Overview of students' balances" iconClass="fas fa-chart-pie" >
      
          <div className="d-flex justify-content-center">
            <Chart options={chartOptions} series={series} type="donut" width="190" />
          </div>

          <div className="bs-divider" />

          <div className="bs-metrics overflow-auto" style={{maxHeight: '125px'}}>
            <div className="bs-metric">
              <div className="bs-label">Gross balance</div>
              <div className="bs-value">
                {currency} {numberFormat(grossBalance)}
              </div>
            </div>

            <div className="bs-metric indigo">
              <div className="bs-label">Current balance</div>
              <div className="bs-value">
                {currency} {numberFormat(currentBalance)}
              </div>
            </div>

            <div className="bs-metric indigo">
              <div className="bs-label">Defaulters</div>
              <span className="bs-pill">{numberFormat(defaulters)}</span>
            </div>
          </div>
      </DashCardHeader>
    </>
  );
};

export default BalanceStatistics;
